import React, { useCallback, useEffect, useState } from 'react';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Link as MuiLink,
	Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useNotify } from 'react-admin';

import {
	associatePartner,
	DEAL_TYPES,
	endAttribution,
	LEGACY_DEAL_TYPE_LABELS,
	listAttributionsByBA,
	IAttribution,
	IPartner,
} from '../../../Services/partners';

import { AssociateDialog } from './AssociateDialog';

// RÈGLE MÉTIER : un client (BA) n'appartient qu'à UN partenaire à la fois.
//
// Ce n'est pas cosmétique : le moteur de commission crée une ligne pour CHAQUE
// attribution active du BA, donc deux attributions = un même paiement client qui paie
// deux partenaires. L'API refuse désormais la seconde ; cet écran l'anticipe (bouton
// d'association désactivé, motif affiché) et fournit le geste manquant — clôturer
// l'attribution en cours — sans lequel la règle rendrait tout transfert impossible.

interface Props {
	billingAccount: { id: string; name?: string };
}

// Libellé du TYPE D'AFFAIRE affiché sur le chip. Le chip portait le « programme » du
// PARTENAIRE — une notion supprimée, et qui de toute façon ne décrivait pas cette
// attribution-ci : un même partenaire peut détenir un apport et une intégration.
const dealTypeLabel = (commissionType?: string) =>
	DEAL_TYPES.find((t) => t.id === commissionType)?.name ||
	(commissionType ? LEGACY_DEAL_TYPE_LABELS[commissionType] : undefined) ||
	commissionType ||
	'—';

export const BillingAccountPartner: React.FC<Props> = ({ billingAccount }) => {
	const notify = useNotify();
	const baId = String(billingAccount.id);

	const [attributions, setAttributions] = useState<IAttribution[]>([]);
	const [open, setOpen] = useState(false);
	const [toEnd, setToEnd] = useState<IAttribution | null>(null);
	const [ending, setEnding] = useState(false);

	const loadAttributions = useCallback(() => {
		listAttributionsByBA(baId)
			.then((rows) => setAttributions(rows || []))
			.catch(() => setAttributions([]));
	}, [baId]);

	useEffect(loadAttributions, [loadAttributions]);

	// Une seule attribution active possible : c'est elle qui bloque toute nouvelle
	// association, et c'est elle qu'on propose de clôturer.
	const activeAttribution = attributions.find((a) => a.status === 'active');

	const confirmEnd = () => {
		if (!toEnd) return;
		setEnding(true);
		endAttribution(toEnd.id)
			.then(() => {
				notify('Attribution clôturée', { type: 'success' });
				setToEnd(null);
				loadAttributions();
			})
			.catch((e: any) => notify(`Erreur : ${e?.message || 'clôture impossible'}`, { type: 'error' }))
			.finally(() => setEnding(false));
	};

	const associate = (partner: IPartner, commissionType: string, rate: number, startAt: string) => {
		associatePartner({ partnerId: partner.id, billingAccountId: baId, commissionType, rate, startAt })
			.then(() => {
				notify('Partenaire associé', { type: 'success' });
				setOpen(false);
				loadAttributions();
			})
			.catch((e: any) => notify(`Erreur : ${e?.message || 'association impossible'}`, { type: 'error' }));
	};

	return (
		<Card style={{ marginBottom: 20, width: '100%' }}>
			<CardContent>
				<Typography variant="h6" gutterBottom>
					Partner
				</Typography>

				{attributions.length === 0 ? (
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
						Aucun partenaire associé à ce billing account.
					</Typography>
				) : (
					<Box sx={{ mb: 1.5 }}>
						{attributions.map((a) => (
							<Box key={a.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
								<Box>
									{/* Le nom mène à la fiche partenaire : depuis un BA, la question qui
									    suit « qui touche la commission ? » est toujours « et lui, c'est
									    qui ? ». Sans ce lien il fallait retenir le nom, ouvrir la liste
									    des partenaires et l'y rechercher. Même convention que le lien
									    vers un projet dans l'onglet Projects. */}
									<MuiLink
										component={RouterLink}
										to={`/partners/${a.partnerId}`}
										variant="body2"
										underline="hover"
										sx={{ fontWeight: 600 }}
									>
										{a.partner?.name || a.partnerId}
									</MuiLink>
									{/* La date lève l'ambiguïté quand un même client porte deux lignes
									    (situation que la règle interdit désormais, mais qui existe encore
									    dans les données antérieures) : sans elle, deux partenaires côte à
									    côte ne disent pas lequel est arrivé en premier. */}
									<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
										{a.startAt ? `Depuis le ${new Date(a.startAt).toLocaleDateString('fr-FR')}` : ''}
										{a.opportunity?.companyName ? ` · Opportunité : ${a.opportunity.companyName}` : ''}
									</Typography>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									{/* Le statut est affiché : sans lui, une attribution clôturée et une
									    attribution active se ressemblent, et on ne comprend pas pourquoi
									    l'association reste bloquée (ou pourquoi elle ne l'est plus). */}
									<Chip
										label={a.status === 'active' ? 'Active' : 'Clôturée'}
										size="small"
										color={a.status === 'active' ? 'success' : 'default'}
									/>
									{/* Termes de CETTE affaire : sans le taux, la ligne ne dit pas ce que
									    touche le partenaire — l'information vivait auparavant sur sa fiche,
									    elle n'existe plus qu'ici. */}
									<Chip
										label={`${dealTypeLabel(a.commissionType)}${a.rate ? ` · ${a.rate}%` : ''}`}
										size="small"
									/>
									{a.status === 'active' && (
										<Button size="small" color="error" startIcon={<LinkOffIcon />} onClick={() => setToEnd(a)}>
											Clôturer
										</Button>
									)}
								</Box>
							</Box>
						))}
					</Box>
				)}

				<Button
					variant="outlined"
					startIcon={<LinkIcon />}
					onClick={() => setOpen(true)}
					fullWidth
					disabled={Boolean(activeAttribution)}
				>
					Associate
				</Button>
				{/* Motif explicite plutôt qu'un bouton grisé et muet : sinon la seule façon de
				    comprendre le blocage serait de tenter l'association et de lire l'erreur. */}
				{activeAttribution && (
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
						Ce client est déjà attribué à {activeAttribution.partner?.name || activeAttribution.partnerId}. Clôture
						cette attribution pour en créer une autre.
					</Typography>
				)}
			</CardContent>

			<Dialog open={Boolean(toEnd)} onClose={() => setToEnd(null)}>
				<DialogTitle>Clôturer cette attribution ?</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						{toEnd?.partner?.name || toEnd?.partnerId} ne percevra plus de commission sur les paiements de ce client
						à partir de maintenant. Les commissions déjà acquises sont conservées.
					</Typography>
					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
						<Button onClick={() => setToEnd(null)}>Annuler</Button>
						<Button variant="contained" color="error" onClick={confirmEnd} disabled={ending}>
							Clôturer
						</Button>
					</Box>
				</DialogContent>
			</Dialog>

			<AssociateDialog open={open} onClose={() => setOpen(false)} onConfirm={associate} />
		</Card>
	);
};
